import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ReactMarkdown from 'react-markdown';

import { getQueryAnswer } from '../api/dashboard';

interface QueryAnswerModalProps {
  open: boolean;
  onClose: () => void;
  traceId: string | null;
  question: string | null;
}

export default function QueryAnswerModal({
  open,
  onClose,
  traceId,
  question,
}: QueryAnswerModalProps) {
  const [answer, setAnswer] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  useEffect(() => {
    if (open && traceId) {
      setIsLoading(true);
      setError(null);
      setAnswer(null);

      getQueryAnswer(traceId)
        .then((result) => {
          if (result) {
            setAnswer(result);
          } else {
            setError('No answer found for this query.');
          }
        })
        .catch((err) => {
          console.error('Failed to fetch answer:', err);
          setError('Failed to load answer. Please try again.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open, traceId]);

  const handleCopy = useCallback(async () => {
    if (!answer) return;

    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setSnackbar({ open: true, message: 'Answer copied to clipboard!' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setSnackbar({ open: true, message: 'Failed to copy.' });
    }
  }, [answer]);

  const handleClose = useCallback(() => {
    setAnswer(null);
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            bgcolor: '#0f172a',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <QuestionAnswerIcon sx={{ color: '#22d3ee' }} />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Query Answer
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {question && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Question
              </Typography>
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: 'rgba(0,0,0,0.2)',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 100,
                  overflow: 'auto',
                }}
              >
                {question}
              </Paper>
            </Box>
          )}

          {isLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={40} sx={{ color: '#22d3ee' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading answer...
              </Typography>
            </Box>
          )}

          {error && !isLoading && (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                color: 'error.main',
              }}
            >
              <Typography>{error}</Typography>
            </Box>
          )}

          {answer && !isLoading && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Generated Answer
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={handleCopy}
                  color={copied ? 'success' : 'inherit'}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </Box>

              <Paper
                sx={{
                  p: 3,
                  bgcolor: '#1e293b',
                  maxHeight: 'calc(90vh - 300px)',
                  overflow: 'auto',
                  borderRadius: 2,
                  '& h1, & h2, & h3': {
                    color: '#f1f5f9',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                    pb: 1,
                    mb: 2,
                  },
                  '& h2': {
                    fontSize: '1.25rem',
                    mt: 3,
                  },
                  '& h3': {
                    fontSize: '1.1rem',
                    mt: 2,
                  },
                  '& p': {
                    color: '#cbd5e1',
                    lineHeight: 1.7,
                    mb: 1.5,
                  },
                  '& ul, & ol': {
                    color: '#cbd5e1',
                    pl: 3,
                  },
                  '& li': {
                    mb: 0.5,
                  },
                  '& strong': {
                    color: '#f1f5f9',
                  },
                  '& code': {
                    bgcolor: 'rgba(148, 163, 184, 0.1)',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.875em',
                  },
                  '& blockquote': {
                    borderLeft: '4px solid #22d3ee',
                    pl: 2,
                    ml: 0,
                    color: '#94a3b8',
                    fontStyle: 'italic',
                  },
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    mb: 2,
                  },
                  '& th, & td': {
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    p: 1,
                    textAlign: 'left',
                  },
                  '& th': {
                    bgcolor: 'rgba(148, 163, 184, 0.1)',
                  },
                }}
              >
                <ReactMarkdown>{answer}</ReactMarkdown>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
